import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import { PeriodTypeView } from '@/types'
import {
  InventoryApproach,
  InventoryGranularity,
  ReceivableGranularity,
  ReceivableApproach,
  PayableGranularity,
  PayableApproach,
  PayableConfigType
} from '../types'

export type PayableConfiguration = Record<
  PayableConfigType,
  {
    id: string
    level_of_granularity: PayableGranularity | null
    modeling_approach: PayableApproach | null
    enabled: boolean
  }
>

type Store = {
  view: PeriodTypeView
  setView: (view: PeriodTypeView) => void
  configuration: {
    inventory: {
      granularity: InventoryGranularity | null
      approach: InventoryApproach | null
    }
    receivable: {
      granularity: ReceivableGranularity | null
      approach: ReceivableApproach | null
    }
    payable: PayableConfiguration
  }
  setInventoryGranularity: (granularity: InventoryGranularity) => void
  setInventoryApproach: (approach: InventoryApproach | null) => void
  setReceivableGranularity: (granularity: ReceivableGranularity) => void
  setReceivableApproach: (approach: ReceivableApproach | null) => void
  setPayableConfiguration: (configuration: PayableConfiguration) => void
}

export const useTwcStore = create<Store>()(
  devtools(
    persist(
      set => ({
        view: `monthly`,
        setView: view => set({ view }),
        configuration: {
          inventory: {
            granularity: null,
            approach: null
          },
          receivable: {
            granularity: null,
            approach: null
          },
          payable: {
            total_costs: {
              id: `total_costs`,
              level_of_granularity: `aggregate_costs`,
              modeling_approach: null,
              enabled: true
            },
            direct_costs: {
              id: `direct_costs`,
              level_of_granularity: null,
              modeling_approach: null,
              enabled: false
            },
            operating_costs: {
              id: `operating_costs`,
              level_of_granularity: null,
              modeling_approach: null,
              enabled: false
            }
          }
        },
        setInventoryGranularity: granularity =>
          set(state => ({
            configuration: {
              ...state.configuration,
              inventory: { ...state.configuration.inventory, granularity }
            }
          })),
        setInventoryApproach: approach =>
          set(state => ({
            configuration: {
              ...state.configuration,
              inventory: { ...state.configuration.inventory, approach }
            }
          })),
        setReceivableGranularity: granularity =>
          set(state => ({
            configuration: {
              ...state.configuration,
              receivable: { ...state.configuration.receivable, granularity }
            }
          })),
        setReceivableApproach: approach =>
          set(state => ({
            configuration: {
              ...state.configuration,
              receivable: { ...state.configuration.receivable, approach }
            }
          })),
        setPayableConfiguration: configuration =>
          set(state => ({
            configuration: {
              ...state.configuration,
              payable: configuration
            }
          }))
      }),
      {
        name: `twc_store`,
        storage: createJSONStorage(() => sessionStorage)
      }
    )
  )
)
